<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import CloneForm from '@client/components/domain-card/modals/clone/clone-form.svelte';
  import { getNotificationsContext } from '@client/components/notifications';
  import { SectionItem } from '@client/components/section';
  import { Select } from '@client/components/select';
  import { Spinner } from '@client/components/spinner';
  import { Step } from '@client/components/steps';

  import { DomainsService } from '@client/services/domains';

  import { appStore } from '@client/stores/app';

  const { addNotification } = getNotificationsContext();

  export let nextDisabled = true;
  export let onExecute = null;
  export let onRollback = null;
  export let step = { data: {} };

  let loading = true;
  let currentStep = 0;
  let maxStepReached = 0;

  // Step 1: Domain selection
  let domains = [];
  let selectedDomain = null;

  // Step 2: Clone form state
  let formRef;
  let error = '';
  let domainname = '';
  let selectedDomains = [];
  let cloneOptions = {};
  let authcode = '';
  let initializing = false;

  const loadDomains = async () => {
    const { data } = await DomainsService.getAll({ screen: 'staff', perPage: 10000 });
    domains = data.map(({ domainname }) => ({
      label: domainname,
      value: domainname
    }));
  };

  const onSelectDomain = (event) => {
    const domain = event.detail;

    if (!domain) {
      domainname = '';
      return;
    }

    domainname = domain.value;
  };

  const handleNext = () => {
    if (selectedDomain) {
      currentStep = 1;
      if (currentStep > maxStepReached) {
        maxStepReached = currentStep;
      }
    }
  };

  const executeCloneDomain = async () => {
    const destdomains = selectedDomains.map(({ value }) => value).join('\n');

    $appStore.loading = true;

    const options = Object.entries(cloneOptions).reduce((acc, [key, value]) => {
      const name =
        key === 'sftp_settings' ||
        key === 'mangoapps_settings' ||
        key === 'workday_settings' ||
        key === 'airtable_settings' ||
        key === 'bamboohr_settings'
          ? 'customdir_settings'
          : key;

      if (value) {
        acc[name] = true;
      }
      return acc;
    }, {});

    const response = await DomainsService.cloneDomain({
      cloneOptions: options,
      destdomains,
      sourcedomain: domainname,
      authcode
    });

    $appStore.loading = false;

    if (response.length) {
      const message = response.shift().join(': ');

      error = $format('label.FAILURE.DOMAIN_CLONE', {
        values: { name: domainname, destination: destdomains.replace(/\n/g, ','), message }
      });

      throw new Error(message);
    }

    addNotification({
      text: $format('label.SUCCESS.CLONE', { values: { type: domainname } }),
      type: 'success',
      autohide: 2500,
      position: 'top-right'
    });

    step.data = {
      id: domainname
    };

    return response;
  };

  onMount(async () => {
    loading = true;

    try {
      await loadDomains();
    } finally {
      loading = false;
    }

    if (onExecute) {
      onExecute(executeCloneDomain);
    }

    if (onRollback) {
      onRollback(null);
    }
  });

  $: isStep0Disabled = !selectedDomain || loading;
  $: isStep1Disabled = !selectedDomains?.length || !domainname || !authcode;

  $: nextDisabled = currentStep !== 1 || isStep1Disabled;

  $: selectedDomainSummary = selectedDomain?.label || $format('label.NOT_SELECTED');
</script>

<div class="domain-clone-form">
  <Step
    active={currentStep === 0}
    disabled={false}
    title={$format('label.DOMAIN')}
    summary={selectedDomainSummary}
    onNext={currentStep === 0 ? handleNext : undefined}
    onStep={() => (currentStep = 0)}
    nextDisabled={isStep0Disabled}
  >
    {#if loading}
      <div class="loading">
        <Spinner show inline />
      </div>
    {:else}
      <SectionItem label={$format('label.DOMAIN')} description={$format('label.CHOOSE_ITEM_TO_CLONE')}>
        <Select
          items={domains}
          bind:value={selectedDomain}
          placeholder={$format('label.PLACEHOLDER.SELECT_DOMAIN')}
          searchable
          clearable={false}
          showChevron
          on:change={onSelectDomain}
        />
      </SectionItem>
    {/if}
  </Step>

  <Step
    active={currentStep === 1}
    disabled={maxStepReached < 1}
    title={$format('label.CLONE_DOMAIN')}
    summary={domainname || $format('label.NOT_CONFIGURED')}
    onBack={currentStep === 1 ? () => (currentStep = 0) : undefined}
    onStep={() => {
      if (currentStep >= 1) currentStep = 1;
    }}
    nextDisabled={isStep1Disabled}
  >
    <CloneForm
      bind:this={formRef}
      bind:domainname
      bind:selectedDomains
      bind:cloneOptions
      bind:authcode
      bind:initializing
      {error}
    />
  </Step>
</div>

<style lang="scss">
  .domain-clone-form {
    .loading {
      display: flex;
      justify-content: center;
      padding: var(--padding-xlarge);
    }
  }
</style>
